import Highcharts from 'highcharts';
import HighchartsCustomEvents from 'highcharts-custom-events';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { TFunction } from 'i18next';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AnalysisLine } from '~src/modules/spidergraph/AnalysisLine';
import { StoreI } from '~src/rootReducer';
import HighchartsReact from '~src/shared/graphs/HighchartsReact';
import {
  isSurveyWithTopics,
  isTopicWithQuestions,
  SpiderGraphState,
} from '~src/store/spidergraph/spidergraph.reducer';

import SpiderChartOptions from './options';
import { ChartContainer } from './spiderChart.sc';
import useScales from '~src/hooks/api/scales/useScales';
import {ESTopic} from "~src/interfaces/topic.interface";
import llsLogger from "~src/services/logger/LlsLogger";
import {ScaleVisualization} from "~src/interfaces/scale.interface";

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsCustomEvents(Highcharts);

type SpiderChartSafe = Required<SpiderGraphState>;

const generateOptions = ({ dimensions, scales, lines }: SpiderChartSafe, t: TFunction): SpiderChartOptions => {
  const gen = new SpiderChartOptions(dimensions, scales, t);
  try {
    lines
      .sort((a, b) => a.getLabel(t).localeCompare(b.getLabel(t)))
      .forEach((line) => {
        gen.addLine(line);
      });

    if (!lines.length) {
      gen.addLine(AnalysisLine.getDummyLine(dimensions));
    }
  } catch (e) {
    console.warn('error creating lines: ', e);
  }
  return gen;
};

function isCompleteState(analysisStore: SpiderGraphState): analysisStore is SpiderChartSafe {
  return Boolean(analysisStore.dimensions && analysisStore.lines && analysisStore.scales);
}

const SpiderChart: FC<{ overrideStyles?: boolean; data?: SpiderGraphState }> = ({
  overrideStyles = false,
  data,
}) => {
  const analysisStore: SpiderGraphState = useSelector((globalStore: StoreI) => globalStore.spidergraph);
  const { t } = useTranslation();
  const { data: scales } = useScales();

  const chartData = data || analysisStore;

  const oneAnswerOptionScaleIds = scales
    ?.filter(scale => scale.labels.length === 1 && scale.visualization === ScaleVisualization.Labels)
    .map(scale => scale.id);

  const isOneAnswerTopic = isTopicWithQuestions(chartData.dimensions)
    ? oneAnswerOptionScaleIds?.includes(chartData.dimensions.topic.scale_id)
    : false;

  const formattedChartData: SpiderGraphState = useMemo(() => {
    if (isTopicWithQuestions(chartData.dimensions)) {
      const scaleId = chartData.dimensions.topic.scale_id
      const scale = scales?.find(s => s.id === scaleId)
      return {...chartData, scales: scale ? [scale] : []};
    }

    if (isSurveyWithTopics(chartData.dimensions)) {
      const topics: ESTopic[] = chartData.dimensions.topics.filter(item => !oneAnswerOptionScaleIds?.includes(item.scale_id));

      return {
        ...chartData,
        scales: scales?.filter(s => Boolean(topics.find(t => t.scale_id === s.id))) || [],
        dimensions: {
          survey: chartData.dimensions.survey,
          topics: topics,
        },
      };
    }

    llsLogger.warn('this code should be unreachable')
    return {scales: [], ...chartData}; // we shouldnt ever go past here

  }, [chartData, oneAnswerOptionScaleIds]);

  if (!isCompleteState(formattedChartData) || isOneAnswerTopic) {
    return null;
  }

  const opts = generateOptions(formattedChartData, t).getOptions();
  return (
    <ChartContainer $overrideStyles={overrideStyles}>
      <HighchartsReact t={t} options={opts} redrawOptions={{ oneToOne: true }} />
    </ChartContainer>
  );
};

export default SpiderChart;
